import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Sparkles, Check, ArrowLeft, CreditCard } from "lucide-react";
import { toast } from "sonner";

const BENEFITS = [
  "All 18 therapy programs unlocked",
  "Unlimited intervention questions",
  "Personalized AI feedback on every answer",
  "Points, streaks, badges & progress dashboard",
  "Weekly goals, treatment plan & daily affirmations",
  "Cancel anytime",
];

export default function Subscribe() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState("");

  const subscribe = async () => {
    setLoading("stripe");
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading("");
    }
  };

  const subscribePaypal = async () => {
    setLoading("paypal");
    try {
      const { data } = await api.post("/paypal/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.approval_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading("");
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-lg mx-auto px-5 py-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="subscribe-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="bg-white rounded-3xl p-8 shadow-sm border border-black/5 animate-fade-up">
          <div className="w-14 h-14 rounded-3xl bg-[#E8A87C] flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-head font-extrabold text-3xl mt-6">Unlock full access</h1>
          <p className="text-muted-foreground mt-2">
            Continue your journey with unlimited access to every program and feature.
          </p>

          <div className="flex items-end gap-2 mt-6">
            <span className="font-head font-extrabold text-5xl">$3</span>
            <span className="text-muted-foreground font-semibold mb-2">/ month</span>
          </div>

          <ul className="mt-6 space-y-3">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-[#48BB78] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
                <span className="text-[15px] text-[#4A5568]">{b}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={subscribe}
            disabled={!!loading}
            className="mt-8 w-full bg-[#2D3748] text-white font-semibold py-4 rounded-full hover:-translate-y-0.5 transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="subscribe-checkout"
          >
            <CreditCard className="w-5 h-5" />
            {loading === "stripe" ? "Redirecting…" : "Pay with card (Stripe)"}
          </button>

          <div className="flex items-center gap-3 my-4">
            <div className="h-px bg-black/10 flex-1" />
            <span className="text-xs text-muted-foreground font-semibold">OR</span>
            <div className="h-px bg-black/10 flex-1" />
          </div>

          <button
            onClick={subscribePaypal}
            disabled={!!loading}
            className="w-full bg-[#FFC439] text-[#003087] font-bold py-4 rounded-full hover:-translate-y-0.5 transition-transform disabled:opacity-60 flex items-center justify-center gap-1"
            data-testid="subscribe-paypal"
          >
            {loading === "paypal" ? (
              "Redirecting…"
            ) : (
              <>
                Pay with <span className="italic font-extrabold">Pay</span>
                <span className="italic font-extrabold text-[#009cde]">Pal</span>
              </>
            )}
          </button>
          <p className="text-xs text-center text-muted-foreground mt-4">
            Secure recurring billing of $3/month. Pay by card via Stripe or with your PayPal account. Cancel anytime.
          </p>
        </div>
      </main>
    </div>
  );
}
